import {
  type IGiftRegistryAdminDetail,
  type IGiftRegistryAdminRefundItem,
  type IGiftRegistryAdminSummary,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type IGiftRegistryState } from './types';

export const preloadedState: IGiftRegistryState = {
  ...asyncStateInitial,
  registries: undefined,
  detail: undefined,
  refundItems: undefined,
};

const giftRegistrySlice = createSlice({
  name: 'GiftRegistry',
  initialState: preloadedState,
  reducers: {
    giftRegistriesLoad(state) {
      startLoading(state);
    },
    giftRegistriesLoadResponse: {
      reducer(
        state,
        action: { payload: { registries: IGiftRegistryAdminSummary[] } },
      ) {
        state.registries = action.payload.registries;
        finishLoading(state);
      },
      prepare(registries: IGiftRegistryAdminSummary[]) {
        return { payload: { registries } };
      },
    },
    giftRegistryDetailLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    giftRegistryDetailLoadResponse: {
      reducer(
        state,
        action: { payload: { detail: IGiftRegistryAdminDetail | null } },
      ) {
        state.detail = action.payload.detail;
        finishLoading(state);
      },
      prepare(detail: IGiftRegistryAdminDetail | null) {
        return { payload: { detail } };
      },
    },
    giftRegistryDetailExtendDeadline: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string, expiresAt: Date) {
        return { payload: { uuid, expiresAt } };
      },
    },
    giftRegistryRefundItemsLoad(state) {
      startLoading(state);
    },
    giftRegistryRefundItemsLoadResponse: {
      reducer(
        state,
        action: { payload: { items: IGiftRegistryAdminRefundItem[] } },
      ) {
        state.refundItems = action.payload.items;
        finishLoading(state);
      },
      prepare(items: IGiftRegistryAdminRefundItem[]) {
        return { payload: { items } };
      },
    },
    giftRegistryRefundContributionMarkRefunded: {
      reducer(state) {
        startLoading(state);
      },
      prepare(contributionUUID: string) {
        return { payload: { contributionUUID } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('GiftRegistry'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  giftRegistriesLoad,
  giftRegistriesLoadResponse,
  giftRegistryDetailLoad,
  giftRegistryDetailLoadResponse,
  giftRegistryDetailExtendDeadline,
  giftRegistryRefundItemsLoad,
  giftRegistryRefundItemsLoadResponse,
  giftRegistryRefundContributionMarkRefunded,
} = giftRegistrySlice.actions;
export default giftRegistrySlice.reducer;
